import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Shield } from "lucide-react";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";

export default function AiQualificationWorkspace({ steps, currentStep, visitedIds, onSelect, children }) {
  const { can, hasAllAccess } = useAuth() || {};
  const canViewSetup = hasAllAccess || (typeof can === "function" && can(FK.AI_AGENT_SETTINGS_VIEW));
  const headingRef = React.useRef(null);
  const previousSection = React.useRef(currentStep.id);
  React.useEffect(() => {
    if (previousSection.current !== currentStep.id) {
      headingRef.current?.focus({ preventScroll: true });
      previousSection.current = currentStep.id;
    }
  }, [currentStep.id]);
  const Icon = currentStep.icon;
  return <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
    <div className="grid lg:grid-cols-[216px_minmax(0,1fr)]">
      <aside className="border-b border-slate-200 bg-slate-50 p-4 lg:border-b-0 lg:border-r">
        <p className="mb-3 px-2 text-xs font-medium text-slate-500">Lead collection</p>
        <nav aria-label="Lead collection sections">
          <ol className="space-y-1">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const selected = step.id === currentStep.id;
              return <li key={step.id}>
                <button type="button" onClick={() => onSelect(step.id)}
                  aria-label={`Step ${index + 1} ${step.title}`}
                  aria-current={selected ? "step" : undefined}
                  className={`flex min-h-12 w-full items-center gap-2.5 rounded-md border px-2.5 py-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 ${selected ? "border-slate-300 bg-white text-slate-900" : "border-transparent text-slate-600 hover:bg-slate-100"}`}>
                  <StepIcon aria-hidden="true" className={`h-4 w-4 shrink-0 ${selected ? "text-emerald-700" : "text-slate-500"}`} />
                  <span className="min-w-0 flex-1">
                    <span className={selected ? "block font-medium" : "block"}>{step.title}</span>
                    {step.optional ? <span className="block text-xs font-normal text-slate-500">Optional</span> : null}
                  </span>
                  {visitedIds.has(step.id) && !selected ? <Check aria-label="Visited" className="h-3.5 w-3.5 text-slate-400" /> : null}
                </button>
              </li>;
            })}
          </ol>
        </nav>
        {canViewSetup ? <nav aria-label="Lead collection resources" className="mt-5 space-y-3 border-t border-slate-200 px-2 pt-4 text-xs text-slate-600">
          <Link to="/app/ai-agent/settings?section=qualification" className="flex items-center gap-2 hover:text-slate-900"><ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />Back to setup</Link>
          <Link to="/app/ai-agent/settings?section=safety" className="flex items-center gap-2 hover:text-slate-900"><Shield aria-hidden="true" className="h-3.5 w-3.5" />Handover settings</Link>
        </nav> : null}
      </aside>
      <div className="min-w-0">
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-emerald-700"><Icon aria-hidden="true" className="h-5 w-5" /></span>
          <div>
            <h2 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-slate-900 focus:outline-none">{currentStep.title}</h2>
            <p className="mt-0.5 text-sm text-slate-600">{currentStep.blurb}</p>
          </div>
        </div>
        <div className="space-y-6 p-5">{children}</div>
      </div>
    </div>
  </div>;
}
